import { useState } from 'react';
import { UNDO_OFFER_MS, type BulkOutcome, type JobOrder } from '@/domain';
import type { BulkResult } from '@/application/ports/JobOrderRepository';
import {
  usePayCases,
  useReleaseCases,
  useUnpayCases,
  useUnreleaseCases,
} from '@/application/usecases/useBulk';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { BulkBar } from '@/ui/data/BulkBar';
import { useMoney } from '@/ui/money/moneyView';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { BulkResultDialog } from '../bulk/BulkResultDialog';
import { useConfirmAction } from '../shell/useConfirmAction';

/** What Accounts may do to many cases at once on a Payments list. */
export type MoneyAction = 'pay' | 'release' | 'unrelease';

const t = strings.payments;

/**
 * The bar over a Payments list while cases are ticked. The lab's request of
 * 17 October 2026, item 9: "Allow the accountant to select multiple cases and
 * mark them as paid, or release", and item 10: "make these actions undoable.
 * and ask for confirmation before each."
 *
 * Each asks first, naming how many cases and, for Mark paid, what they come
 * to; each is then said in a toast that carries Undo for about ten seconds,
 * which takes back exactly the cases that went. A case that could not be done
 * is said with why, grouped as every selection says it, and the rest go.
 */
export function MoneySelectionActions({
  rows,
  actions,
  onClear,
  onSelectPage,
}: {
  /** The ticked cases, as they were when ticked. */
  rows: readonly JobOrder[];
  actions: readonly MoneyAction[];
  onClear: () => void;
  onSelectPage?: () => void;
}) {
  const pay = usePayCases();
  const unpay = useUnpayCases();
  const release = useReleaseCases();
  const unrelease = useUnreleaseCases();
  const ask = useConfirmAction();
  const money = useMoney();
  const [answer, setAnswer] = useState<{ verb: string; outcomes: BulkOutcome[] } | null>(null);

  const count = rows.length;
  const jobOrderNos = rows.map((row) => row.jobOrderNo);
  // What the ticked cases still owe, which is what Mark paid receives.
  const owed = rows.reduce((sum, row) => sum + (row.payment.balance ?? row.value ?? 0), 0);
  const busy = pay.isPending || unpay.isPending || release.isPending || unrelease.isPending;

  /**
   * Said once it is done: the cases that went, with Undo for them alone, and
   * the ones that did not with why.
   */
  function finish(result: BulkResult, verb: string, undo: (done: string[]) => Promise<BulkResult>, undone: string) {
    onClear();
    if (result.refused > 0) setAnswer({ verb, outcomes: result.results });
    const done = result.results.filter((outcome) => outcome.ok).map((outcome) => outcome.id);
    if (done.length === 0) return;
    toast(t.manyDone(done.length, verb), {
      tone: 'success',
      durationMs: UNDO_OFFER_MS,
      action: {
        label: strings.undo.action,
        onClick: () => {
          // By the promise: the toast outlives the screen, and its answer must still be said.
          undo(done).then(
            (back) => toast(t.manyDone(back.done, undone)),
            (error: Error) => toast(failureWords(error), { tone: 'error' }),
          );
        },
      },
    });
  }

  if (count === 0 && answer === null) return null;

  return (
    <>
      {count > 0 && (
        <BulkBar count={count} onClear={onClear} onSelectPage={onSelectPage}>
          {actions.includes('pay') && (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() =>
                ask(
                  strings.confirm.payMany(count, money(owed)),
                  () =>
                    pay
                      .mutateAsync(jobOrderNos)
                      .then((result) => finish(result, t.verbPaid, unpay.mutateAsync, t.verbToBePaid)),
                  { confirmLabel: t.markPaid },
                )
              }
            >
              {t.markPaid}
            </Button>
          )}
          {actions.includes('release') && (
            <Button
              disabled={busy}
              onClick={() =>
                ask(
                  strings.confirm.releaseMany(count),
                  () =>
                    release
                      .mutateAsync(jobOrderNos)
                      .then((result) => finish(result, t.verbReleased, unrelease.mutateAsync, t.verbNotReleased)),
                  { confirmLabel: t.releaseToShip },
                )
              }
            >
              {t.releaseToShip}
            </Button>
          )}
          {actions.includes('unrelease') && (
            <Button
              disabled={busy}
              onClick={() =>
                ask(
                  strings.confirm.takeBackMany(count),
                  () =>
                    unrelease
                      .mutateAsync(jobOrderNos)
                      .then((result) => finish(result, t.verbNotReleased, release.mutateAsync, t.verbReleased)),
                  { confirmLabel: t.takeBackRelease },
                )
              }
            >
              {t.takeBackRelease}
            </Button>
          )}
        </BulkBar>
      )}

      {answer !== null && (
        <BulkResultDialog verb={answer.verb} outcomes={answer.outcomes} onClose={() => setAnswer(null)} />
      )}
    </>
  );
}
